import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, ClipboardCheck, Filter, Target, Timer, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { countMyAttempts } from '../lib/studentTestApi';
import { loadTestsPage, type TestTab } from '../lib/data';
import { countClass } from '../lib/parse';
import type { TestInfo } from '../lib/types';
import { Badge, Button, EmptyState, LoadingState, SearchField, SectionHeading } from '../components/ui';

const PAGE_SIZE = 15;

export function StudentTests() {
  const navigate = useNavigate();
  const [tests, setTests] = useState<TestInfo[]>([]);
  const [totalInView, setTotalInView] = useState(0);
  const [availableTotal, setAvailableTotal] = useState<number>();
  const [attempts, setAttempts] = useState<number>();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<TestTab>('all');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(query), 350);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => { setPage(0); }, [search, tab]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void (async () => {
      try {
        const [{ items, total }, attemptCount] = await Promise.all([
          loadTestsPage({ page, pageSize: PAGE_SIZE, search, tab }),
          page === 0 && !search && tab === 'all' ? countMyAttempts() : Promise.resolve(attempts),
        ]);
        if (!active) return;
        setTests(items);
        setTotalInView(total);
        if (page === 0 && !search && tab === 'all') setAttempts(attemptCount);
        if (availableTotal === undefined) {
          const available = await countClass('test_TestInfo', { where: (q) => { q.equalTo('IsPublished', true); q.notEqualTo('Type', 'Folder'); } });
          if (active) setAvailableTotal(available);
        }
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, tab]);

  const pageCount = Math.max(1, Math.ceil(totalInView / PAGE_SIZE));
  const rangeLabel = useMemo(() => {
    if (!totalInView) return '0 tests';
    const start = page * PAGE_SIZE + 1;
    const end = Math.min(totalInView, (page + 1) * PAGE_SIZE);
    return `${start}\u2013${end} of ${totalInView} tests`;
  }, [page, totalInView]);

  const resetFilters = () => { setQuery(''); setSearch(''); setTab('all'); setPage(0); };

  return <div className="tests-page">
    <section className="page-title-row"><div><h1>Tests</h1><p>Practice with the patterns that matter most</p></div><button className="round-button" aria-label="Reset filters" onClick={resetFilters}><Filter size={19} /></button></section>
    <SearchField value={query} onChange={setQuery} placeholder="Search tests by title" />
    <div className="segmented-tabs">{([['all', 'All tests'], ['practice', 'Practice'], ['exam', 'Exam mode']] as Array<[TestTab, string]>).map(([key, label]) => <button key={key} className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>{label}</button>)}</div>
    <section className="test-stats">
      <div><Target size={18} /><strong>{availableTotal ?? '\u2014'}</strong><span>Available</span></div>
      <div><Trophy size={18} /><strong>{attempts ?? '\u2014'}</strong><span>Your attempts</span></div>
      <div><ClipboardCheck size={18} /><strong>{totalInView || '0'}</strong><span>In this view</span></div>
    </section>
    <section className="test-list-section">
      <SectionHeading title={`Available tests \u00b7 ${rangeLabel}`} action={resetFilters} actionLabel="View all" />
      {loading ? <LoadingState /> : tests.length
        ? <div className="test-list">{tests.map((test) => <article className="test-card" key={test.objectId}>
            <div className="test-icon"><ClipboardCheck size={23} /></div>
            <div className="test-copy">
              <div><Badge tone={test.TestPaidType === 'Free' ? 'green' : 'teal'}>{test.TestPaidType ?? 'Paid'}</Badge><span className="test-course">{test.CourseTitle ?? 'AyurGuide tests'}</span></div>
              <h3>{test.TestTitle}</h3>
              <div className="test-meta"><span><ClipboardCheck size={14} /> {Number(test.NoOfQuestions ?? 0) || '\u2014'} questions</span><span><Timer size={14} /> {Number(test.Time ?? 60)} min</span><span><Trophy size={14} /> {test.TotalMarks ?? '\u2014'} marks</span></div>
            </div>
            <Button variant="soft" icon={ArrowRight} onClick={() => navigate(`/tests/${test.objectId}`)}>Start</Button>
          </article>)}</div>
        : <EmptyState title="No tests found" description="Try another search or switch the test filter." action={<Button variant="soft" onClick={resetFilters}>Clear filters</Button>} />}
      <div className="pagination-row">
        <Button variant="ghost" icon={ChevronLeft} disabled={page === 0 || loading} onClick={() => setPage((value) => Math.max(0, value - 1))}>Previous</Button>
        <span className="pagination-label">Page {page + 1} of {pageCount}</span>
        <Button variant="ghost" disabled={page + 1 >= pageCount || loading} onClick={() => setPage((value) => value + 1)}>Next <ChevronRight size={16} /></Button>
      </div>
    </section>
  </div>;
}
